import type { ApiPaginatedResponse, ApiResponse, ApiResult } from '@bcube/shared';
import { api } from '@/lib/api';
import type {
  CreateCategoryDto,
  CreateProductDto,
  IProduct,
  IProductCategory,
} from '@/types/commercial/product';

export interface FindManyProductsParams {
  filter?: {
    search?: string;
    category?: string;
    categoryCode?: string;
  };
  page?: number;
  limit?: number;
}

export interface FindManyCategoriesParams {
  filter?: {
    search?: string;
  };
  page?: number;
  limit?: number;
}

export interface PaginatedResponse<T> {
  docs: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const productApi = {
  // Product methods
  findMany: async (params?: FindManyProductsParams): Promise<PaginatedResponse<IProduct>> => {
    const queryParams: Record<string, unknown> = {};

    if (params?.page !== undefined) queryParams.page = params.page;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    if (params?.filter?.search) queryParams.search = params.filter.search;
    if (params?.filter?.category) queryParams.category_id = params.filter.category;
    if (params?.filter?.categoryCode) queryParams.category_code = params.filter.categoryCode;

    const { data } = await api.get<ApiPaginatedResponse<IProduct>>('/commercial/products', {
      params: queryParams,
    });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      limit: data.meta.limit,
      totalPages: data.meta.pages,
    };
  },

  findOne: async (id: string): Promise<IProduct> => {
    const { data } = await api.get<ApiResponse<IProduct>>(`/commercial/products/${id}`);
    return data.data;
  },

  create: async (payload: CreateProductDto): Promise<ApiResult<IProduct>> => {
    try {
      const { data } = await api.post<ApiResponse<IProduct>>('/commercial/products', payload);
      return { success: true, data: data.data };
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          global: (error as any)?.response?.data?.message || 'Erro ao criar produto',
          fields: (error as any)?.response?.data?.fields,
        },
      };
    }
  },

  update: async (id: string, payload: Partial<CreateProductDto>): Promise<ApiResult<IProduct>> => {
    try {
      const { data } = await api.patch<ApiResponse<IProduct>>(
        `/commercial/products/${id}`,
        payload,
      );
      return { success: true, data: data.data };
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          global: (error as any)?.response?.data?.message || 'Erro ao atualizar produto',
          fields: (error as any)?.response?.data?.fields,
        },
      };
    }
  },

  delete: async (id: string): Promise<ApiResult> => {
    try {
      await api.delete(`/commercial/products/${id}`);
      return { success: true };
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          global: (error as any)?.response?.data?.message || 'Erro ao excluir produto',
        },
      };
    }
  },

  // Category methods
  findManyCategories: async (
    params?: FindManyCategoriesParams,
  ): Promise<PaginatedResponse<IProductCategory>> => {
    const queryParams: Record<string, unknown> = {};

    if (params?.page !== undefined) queryParams.page = params.page;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    if (params?.filter?.search) queryParams.search = params.filter.search;

    const { data } = await api.get<ApiPaginatedResponse<IProductCategory>>(
      '/commercial/products/categories',
      { params: queryParams },
    );
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      limit: data.meta.limit,
      totalPages: data.meta.pages,
    };
  },

  findAllCategories: async (): Promise<IProductCategory[]> => {
    const { data } = await api.get<ApiPaginatedResponse<IProductCategory>>(
      '/commercial/products/categories',
      { params: { limit: 1000 } },
    );
    return data.data;
  },

  findOneCategory: async (id: string): Promise<IProductCategory> => {
    const { data } = await api.get<ApiResponse<IProductCategory>>(
      `/commercial/products/categories/${id}`,
    );
    return data.data;
  },

  createCategory: async (payload: CreateCategoryDto): Promise<ApiResult<IProductCategory>> => {
    try {
      const { data } = await api.post<ApiResponse<IProductCategory>>(
        '/commercial/products/categories',
        payload,
      );
      return { success: true, data: data.data };
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          global: (error as any)?.response?.data?.message || 'Erro ao criar categoria',
          fields: (error as any)?.response?.data?.fields,
        },
      };
    }
  },

  updateCategory: async (
    id: string,
    payload: Partial<CreateCategoryDto>,
  ): Promise<ApiResult<IProductCategory>> => {
    try {
      const { data } = await api.patch<ApiResponse<IProductCategory>>(
        `/commercial/products/categories/${id}`,
        payload,
      );
      return { success: true, data: data.data };
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          global: (error as any)?.response?.data?.message || 'Erro ao atualizar categoria',
          fields: (error as any)?.response?.data?.fields,
        },
      };
    }
  },

  deleteCategory: async (id: string): Promise<ApiResult> => {
    try {
      await api.delete(`/commercial/products/categories/${id}`);
      return { success: true };
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          global:
            (error as any)?.response?.data?.message ||
            'Erro ao excluir categoria. Verifique se não há produtos vinculados.',
        },
      };
    }
  },
};
